// Drawer «Nueva mantención» (handoff §6): Nombre, Cada + Unidad y «Aplica
// a» → POST /maintenance-items. «Aplica a» ofrece lo que el backend
// modela al crear: todas las tiendas (scope brand, brandId null) o toda
// una marca (scope brand con brandId), sólo si la organización tiene
// marcas. El handoff lista también las zonas; el catálogo no tiene
// alcance por zona (sólo brand | assets) y una foto de las tiendas de la
// zona no sería lo mismo, así que la opción no se ofrece: las tiendas
// específicas se eligen después con «Asignar tiendas». El campo no lleva
// hint que lo explique: el prototipo no lo tiene y el handoff pide
// preguntar antes de agregar copy (la pregunta está en open_questions).
//
// «Crear» espera a que se conozca cuántas tiendas alcanza el ítem, así el
// toast del handoff («Mantención creada en N tiendas») siempre trae el
// número; sólo si ese dato falló o la receta no lo tiene cae a
// «Mantención creada» (anotado en deviations).
//
// Sin «Proveedor sugerido» ni «Avisar antes» (ver EditarMantencionDrawer).

import { useId, useState } from "react";
import type { MaintenanceItemCreateBody } from "@bitacorapro/api-types";
import { Drawer } from "../../../components/Drawer";
import { ApiError } from "../../../lib/api/error";
import { CadenceFields } from "./CadenceFields";
import { composeCadence, isValidEvery, type CadenceUnit } from "./cadence";
import { useCreateMaintenanceItem } from "./queries";
import type { MantencionWords } from "./words";

export type NuevaMantencionDrawerProps = {
  words: MantencionWords;
  /** Las marcas de la organización (GET /organizations/me); vacío = sin opción de marca. */
  brands: ReadonlyArray<{ id: string; name: string }>;
  /** Cuántas tiendas alcanza el ítem (todas, o las de una marca); null si no se sabe. */
  storeCountFor: (brandId: string | null) => number | null;
  /** El conteo de tiendas aún está cargando: «Crear» espera. */
  storesLoading: boolean;
  onClose: () => void;
  onSaved: (message: string) => void;
};

const ALL_STORES = "";

export function NuevaMantencionDrawer({
  words,
  brands,
  storeCountFor,
  storesLoading,
  onClose,
  onSaved,
}: NuevaMantencionDrawerProps) {
  const [label, setLabel] = useState("");
  const [every, setEvery] = useState("3");
  const [unit, setUnit] = useState<CadenceUnit>("months");
  const [brandId, setBrandId] = useState<string>(ALL_STORES);
  const [error, setError] = useState<string | null>(null);
  const create = useCreateMaintenanceItem();
  const nameId = useId();
  const appliesId = useId();
  const errorId = useId();

  const trimmedLabel = label.trim();
  const canCreate =
    !create.isPending && !storesLoading && trimmedLabel.length > 0 && isValidEvery(every);

  async function submit() {
    setError(null);
    const resolvedBrandId = brandId === ALL_STORES ? null : brandId;
    const body: MaintenanceItemCreateBody = {
      brandId: resolvedBrandId,
      label: trimmedLabel,
      cadenceDays: composeCadence({ every: Number(every), unit }),
      scope: "brand",
    };
    try {
      await create.mutateAsync(body);
      const count = storeCountFor(resolvedBrandId);
      onSaved(
        count === null
          ? "Mantención creada"
          : `Mantención creada en ${count} ${count === 1 ? words.assetLower : words.assetPluralLower}`
      );
      onClose();
    } catch (err) {
      setError(
        err instanceof ApiError && err.status >= 400 && err.status < 500
          ? "No pudimos crear la mantención: revisa el nombre y la frecuencia."
          : "No pudimos crear la mantención. Intenta de nuevo."
      );
    }
  }

  return (
    <Drawer
      title="Nueva mantención"
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            className="btn btn--ghost"
            title="Cerrar sin crear la mantención"
            disabled={create.isPending}
            onClick={onClose}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn--primary"
            title={`Crear esta mantención en las ${words.assetPluralLower} elegidas`}
            disabled={!canCreate}
            onClick={() => void submit()}
          >
            {create.isPending ? "Creando…" : "Crear"}
          </button>
        </>
      }
    >
      {error && (
        <p id={errorId} className="auth-error" role="alert">
          {error}
        </p>
      )}
      <div className="field ag-field">
        <label className="field__label" htmlFor={nameId}>
          Nombre
        </label>
        <input
          id={nameId}
          className="input"
          placeholder="Ej. Limpieza de ductos"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          disabled={create.isPending}
          maxLength={200}
          aria-describedby={error ? errorId : undefined}
        />
      </div>
      <CadenceFields
        every={every}
        unit={unit}
        onEveryChange={setEvery}
        onUnitChange={setUnit}
        disabled={create.isPending}
      />
      <div className="field ag-field">
        <label className="field__label" htmlFor={appliesId}>
          Aplica a
        </label>
        <select
          id={appliesId}
          className="select"
          value={brandId}
          onChange={(e) => setBrandId(e.target.value)}
          disabled={create.isPending}
        >
          <option value={ALL_STORES}>Todas las {words.assetPluralLower}</option>
          {brands.map((b) => (
            <option key={b.id} value={b.id}>
              Marca {b.name}
            </option>
          ))}
        </select>
      </div>
    </Drawer>
  );
}
